/**
 * The new Deal Insights page (EXCH-19435).
 *
 * It lives in parallel with the old `DealInsights/` directory (served at `/deal-insights`) until release, after
 * which the old directory is removed and this one takes over.
 *
 * The design-system theme covers the header and the General tab only; the Geolocation tab still renders old
 * components that read old theme keys, so it stays outside the boundary.
 */
import Box from '@mui/material/Box';
import { ThemeProvider } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { LazyLoadable } from '@openx/components-core/lib/LazyLoadable/LazyLoadable.tsx';
import {
  AdapterDateFns,
  LocalizationProvider,
} from '@openx/design-system/components/DatePicker/LocalizationProvider.tsx';
import { theme as designSystemTheme } from '@openx/design-system/theme/theme.ts';
import type {} from '@openx/design-system/theme/tokens/typography.ts';
import { type ReactNode, useMemo } from 'react';
import { Navigate, Route, Routes } from 'react-router';
import { DealInsightsProvider } from '../DealInsights/context/DealInsightsContext.tsx';
import { DealInsightsV2Header } from './components/DealInsightsV2Header/DealInsightsV2Header.tsx';
import { DealInsightsV2Section } from './components/sections/DealInsightsV2Section.tsx';
import { OverviewSection } from './components/sections/OverviewSection.tsx';
import { DEAL_INSIGHTS_V2_PATH, DEAL_INSIGHTS_V2_PATHS } from './constants.ts';
import { useDealInsightsV2Context } from './context/DealInsightsV2Context.tsx';

// Deferred: pulls in the chart.js/chartjs-chart-geo/d3-geo/topojson map-rendering stack, only needed on this one tab
const DealInsightsGeolocation = LazyLoadable(
  () => import('../DealInsights/components/DealInsightsGeolocation/DealInsightsGeolocation.tsx'),
  'DealInsightsGeolocation'
);

/**
 * Scopes the design-system theme (and the date-picker localization its pickers throw without) to its children
 * only, so the rest of the app, and the old Geolocation tab, keep rendering in the app's current theme.
 * No `CssBaseline` on purpose: it injects global styles that would leak.
 */
export const DesignSystemBoundary = ({ children }: { children: ReactNode }) => (
  <ThemeProvider theme={designSystemTheme}>
    <LocalizationProvider dateAdapter={AdapterDateFns}>{children}</LocalizationProvider>
  </ThemeProvider>
);

const General = () => {
  // Static placeholders: each of these sections gets its own data request in a follow-up task.
  const placeholderSections = [
    {
      key: 'performance-trends',
      title: 'Performance Trends',
      emptyMessage: 'No trend data is available for the selected date range and filters.',
    },
    {
      key: 'media-mix-breakdown',
      title: 'Media Mix Breakdown',
      emptyMessage: 'No media mix data is available for the selected date range and filters.',
    },
    {
      key: 'performance',
      title: 'Performance',
      emptyMessage: 'No results match the selected date range and filters.',
    },
  ];

  return (
    <Box
      sx={{ display: 'flex', flexDirection: 'column', gap: 2, px: 3, py: 2, backgroundColor: 'background.default' }}
      data-test="deal-insights-v2-general"
    >
      <OverviewSection />
      {placeholderSections.map(({ key, title, emptyMessage }) => (
        <DealInsightsV2Section
          key={key}
          title={title}
          state="content"
          emptyMessage={emptyMessage}
          data-test={`deal-insights-v2-${key}`}
        >
          <Typography variant="body1" color="textSecondary">
            Coming soon
          </Typography>
        </DealInsightsV2Section>
      ))}
    </Box>
  );
};

/**
 * Temporary bridge: renders the OLD Geolocation tab inside the OLD provider seeded with the V2 date range and
 * filters. The `key` remounts the old provider (and so reloads its data) whenever the V2 query params change.
 */
const Geolocation = () => {
  const { dateRange, filters, queryParams } = useDealInsightsV2Context();
  const initialState = useMemo(() => ({ dateRange, filters }), [dateRange, filters]);

  return (
    <DealInsightsProvider key={JSON.stringify(queryParams)} initialState={initialState}>
      <DealInsightsGeolocation />
    </DealInsightsProvider>
  );
};

export const DealInsightsV2 = () => (
  <>
    <DesignSystemBoundary>
      <DealInsightsV2Header />
    </DesignSystemBoundary>
    <Routes>
      <Route
        index
        element={
          <DesignSystemBoundary>
            <General />
          </DesignSystemBoundary>
        }
      />
      <Route path={DEAL_INSIGHTS_V2_PATHS.geolocation} element={<Geolocation />} />
      <Route path="*" element={<Navigate to={DEAL_INSIGHTS_V2_PATH} replace />} />
    </Routes>
  </>
);
